import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { CartPanelCheckoutSummary } from '../utils/cartPanelUtils'

interface CartCheckoutCopy {
  checkoutLabel: string
  blockerNote: string | undefined
}

export function useCartCheckoutCopy(
  isLoggedIn: boolean,
  { readyItemCount, blockedItemCount, attentionItemCount }: CartPanelCheckoutSummary,
): CartCheckoutCopy {
  const intl = useIntl()

  return useMemo(() => {
    const blockerNote = attentionItemCount > 0
      ? intl.formatMessage(
          {
            id: '72DOtW_TiEpooT_ZgfDH',
            defaultMessage: '{count, plural, one {# item needs details before it can be booked} other {# items need details before they can be booked}}',
            description: 'Cart footer message below checkout explaining how many trip items need more details before booking',
          },
          { count: attentionItemCount },
        )
      : undefined

    if (!isLoggedIn) {
      return {
        checkoutLabel: intl.formatMessage({
          id: 'PsWbPJA5WZimhO6Dv0Zl',
          defaultMessage: 'Log in to checkout',
          description: 'Cart panel checkout button text when user is not logged in',
        }),
        blockerNote,
      }
    }

    if (blockedItemCount > 0 && readyItemCount > 0) {
      return {
        checkoutLabel: intl.formatMessage(
          {
            id: '20LBeLbhTvcQwp3VNyhd',
            defaultMessage: 'Book {bookable} of {total}',
            description: 'Trip planner cart checkout button, in the cart panel and the chat cart, when only some items are ready, showing how many will be booked',
          },
          // Deselected items are neither ready nor blocked, so counting them in the
          // denominator would quote a total the customer never asked to book.
          { bookable: readyItemCount, total: readyItemCount + blockedItemCount },
        ),
        blockerNote,
      }
    }

    return {
      checkoutLabel: intl.formatMessage({
        id: '7Ej3duOhiVx-nzIcPorw',
        defaultMessage: 'Book now',
        description: 'Primary checkout button text shown in the trip planner cart panel footer',
      }),
      blockerNote,
    }
  }, [intl, isLoggedIn, readyItemCount, blockedItemCount, attentionItemCount])
}
